import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  AiCreditPack,
  AiCreditTransaction,
  AiCreditsStatus,
  AiCreditsUsageStats,
  AiPricingInfo,
} from '@/types/ai-credits';
import type { PaginatedResponse } from '@/lib/api';

export const AI_CREDITS_STATUS_QUERY_KEY = ['ai', 'credits', 'status'] as const;

type AiCreditsStatusBody = {
  available?: boolean;
  unlimited?: boolean;
  balance?: unknown;
  balance_details?: unknown;
};

function extractBalanceValue(input: unknown): number | undefined {
  if (typeof input === 'number' && Number.isFinite(input)) {
    return input;
  }

  if (typeof input === 'object' && input !== null) {
    const maybeBalance = (input as { balance?: unknown }).balance;
    if (typeof maybeBalance === 'number' && Number.isFinite(maybeBalance)) {
      return maybeBalance;
    }
  }

  return undefined;
}

export function parseCreditsStatusBody(data: unknown): AiCreditsStatus | null {
  if (typeof data !== 'object' || data === null) {
    return null;
  }

  const body = data as AiCreditsStatusBody;
  const parsedBalance =
    extractBalanceValue(body.balance) ?? extractBalanceValue(body.balance_details);
  const unlimited = body.unlimited === true;

  const status: AiCreditsStatus = {
    available:
      typeof body.available === 'boolean'
        ? body.available
        : parsedBalance !== undefined
          ? parsedBalance > 0 || unlimited
          : unlimited,
    unlimited,
  };

  if (parsedBalance !== undefined) {
    status.balance = parsedBalance;
  }

  return status;
}

export function extractCreditsStatus(headers: Headers): AiCreditsStatus | null {
  const available = headers.get('X-AI-Credits-Available');
  const unlimited = headers.get('X-AI-Credits-Unlimited');
  const balance = headers.get('X-AI-Credits-Balance');

  if (available === null && unlimited === null) return null;

  const status: AiCreditsStatus = {
    available: available === 'true',
    unlimited: unlimited === 'true',
  };

  if (balance !== null) {
    const parsed = Number.parseInt(balance, 10);
    if (!Number.isNaN(parsed)) {
      status.balance = parsed;
    }
  }

  return status;
}

export function useAiCreditsStatus() {
  return useQuery({
    queryKey: AI_CREDITS_STATUS_QUERY_KEY,
    queryFn: async (): Promise<AiCreditsStatus | null> => {
      const response = await api.getWithMeta<AiCreditsStatusBody>('/ai/credits/balance');
      const statusFromHeaders = extractCreditsStatus(response.headers);
      if (statusFromHeaders) return statusFromHeaders;

      // Fallback depuis le body si les headers ne sont pas présents
      return parseCreditsStatusBody(response.body.data);
    },
    staleTime: 30_000,
  });
}

export function useAiCreditTransactions(filters?: {
  page?: number;
  per_page?: number;
  type?: string;
}) {
  const params = new URLSearchParams();
  if (filters?.page) params.set('page', String(filters.page));
  if (filters?.per_page) params.set('per_page', String(filters.per_page));
  if (filters?.type) params.set('type', filters.type);
  const qs = params.toString();

  return useQuery({
    queryKey: ['ai', 'credits', 'transactions', filters],
    queryFn: async () => {
      const response = await api.get<PaginatedResponse<AiCreditTransaction>>(
        `/ai/credits/transactions${qs ? `?${qs}` : ''}`,
      );
      return response as unknown as PaginatedResponse<AiCreditTransaction>;
    },
  });
}

export function useAiCreditPacks() {
  return useQuery({
    queryKey: ['ai', 'credits', 'packs'],
    queryFn: async () => {
      const response = await api.get<AiCreditPack[]>('/ai/credits/packs');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useAiPricingTiers() {
  return useQuery({
    queryKey: ['ai', 'credits', 'pricing'],
    queryFn: async () => {
      const response = await api.get<AiPricingInfo>('/ai/credits/pricing');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
  });
}

export function useAiCreditsUsage(filters?: { period?: '7d' | '30d' | '90d' }) {
  const params = new URLSearchParams();
  if (filters?.period) params.set('period', filters.period);
  const qs = params.toString();

  return useQuery({
    queryKey: ['ai', 'credits', 'usage', filters],
    queryFn: async () => {
      const response = await api.get<AiCreditsUsageStats>(`/ai/credits/usage${qs ? `?${qs}` : ''}`);
      return response.data;
    },
  });
}

export interface AiCreditPurchase {
  id: string;
  amount: string;
  currency: string;
  status: 'pending' | 'completed' | 'failed';
  credits: number | null;
  gateway_transaction_id: string | null;
  paid_at: string | null;
  created_at: string;
}

export function useAiCreditPurchaseHistory(page = 1) {
  return useQuery({
    queryKey: ['ai', 'credits', 'purchases', page],
    queryFn: async () => {
      const response = await api.get<PaginatedResponse<AiCreditPurchase>>(
        `/ai/credits/purchases?page=${page}`,
      );
      return response as unknown as PaginatedResponse<AiCreditPurchase>;
    },
  });
}

export interface AiCreditPaymentStatus {
  status: 'pending' | 'completed' | 'failed';
  amount: string;
  currency: string;
  credits: number | null;
  paid_at: string | null;
}

export function useAiCreditPaymentStatus(transactionId: string | null) {
  return useQuery({
    queryKey: ['ai', 'credits', 'payment-status', transactionId],
    queryFn: async (): Promise<AiCreditPaymentStatus> => {
      const response = await api.get<AiCreditPaymentStatus>(
        `/ai/credits/purchase/${transactionId}/status`,
      );
      return response.data;
    },
    enabled: transactionId !== null && transactionId !== '',
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'completed' || status === 'failed') return false;
      return 3000;
    },
  });
}
